import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { visaProgramAPI } from '../utils/api';
import { Plus, Globe, Calendar, Edit, Trash2, X, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminVisaPrograms() {
  const [programs, setPrograms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    visa_type: 'Visitor Visa',
    description: '',
    processing_time_days: ''
  });

  const visaTypes = ['Visitor Visa', 'Student Visa', 'Dependent Visa', 'Investor Visa', 'Startup Visa', 'Permanent Residency'];
  const countries = ['Canada', 'Australia', 'New Zealand', 'United States', 'United Kingdom', 'Europe (Schengen)', 'Germany', 'Singapore', 'UAE', 'Other'];

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const res = await visaProgramAPI.getPrograms();
      setPrograms(res.data);
    } catch (error) {
      toast.error('Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      country: '',
      visa_type: 'Visitor Visa',
      description: '',
      processing_time_days: ''
    });
    setEditingProgram(null);
    setShowForm(false);
  };

  const handleEdit = (program) => {
    setFormData({
      name: program.name,
      country: program.country,
      visa_type: program.visa_type,
      description: program.description,
      processing_time_days: program.processing_time_days.toString()
    });
    setEditingProgram(program);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        processing_time_days: parseInt(formData.processing_time_days)
      };

      if (editingProgram) {
        // Update existing program
        await visaProgramAPI.updateProgram(editingProgram.id, payload);
        toast.success('Visa program updated successfully!');
      } else {
        // Create new program
        await visaProgramAPI.createProgram(payload);
        toast.success('Visa program created successfully!');
      }
      
      resetForm();
      fetchPrograms();
    } catch (error) {
      toast.error(editingProgram ? 'Failed to update program' : 'Failed to create program');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (programId) => {
    if (!window.confirm('Are you sure you want to delete this visa program? This action cannot be undone.')) {
      return;
    }
    
    try {
      await visaProgramAPI.deleteProgram(programId);
      toast.success('Visa program deleted successfully');
      fetchPrograms();
    } catch (error) {
      toast.error('Failed to delete program');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="main-content flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-300 border-t-slate-800"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content" data-testid="admin-visa-programs-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="programs-title">Visa Programs</h1>
            <p className="text-slate-500 text-sm">Configure available visa programs for clients</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                resetForm();
                setShowForm(!showForm);
              }}
              data-testid="add-program-button"
              className="px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-all flex items-center gap-2"
            >
              <Plus size={18} />
              Add Program
            </button>
            <div className="w-px h-8 bg-slate-200"></div>

          </div>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="program-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">
                {editingProgram ? 'Edit Visa Program' : 'New Visa Program'}
              </h2>
              <button
                onClick={resetForm}
                className="p-2 hover:bg-slate-100 rounded-lg transition-all"
              >
                <X size={20} className="text-slate-500" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Program Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  data-testid="input-program-name"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none"
                  placeholder="e.g., Canada Visitor Visa - Tourist"
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Country *</label>
                  <select
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    data-testid="select-country"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none"
                    required
                  >
                    <option value="">Select Country</option>
                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Visa Type *</label>
                  <select
                    value={formData.visa_type}
                    onChange={(e) => setFormData({...formData, visa_type: e.target.value})}
                    data-testid="select-visa-type"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none"
                    required
                  >
                    {visaTypes.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  data-testid="input-description"
                  rows="3"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none"
                  placeholder="Describe the program requirements and benefits"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Processing Time (days) *</label>
                <input
                  type="number"
                  value={formData.processing_time_days}
                  onChange={(e) => setFormData({...formData, processing_time_days: e.target.value})}
                  data-testid="input-processing-time"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none"
                  placeholder="e.g., 30"
                  min="1"
                  required
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="submit-program-button"
                  className="px-6 py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      {editingProgram ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {editingProgram ? 'Update Program' : 'Create Program'}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  data-testid="cancel-button"
                  className="px-6 py-2.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Programs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="programs-grid">
          {programs.length === 0 ? (
            <div className="col-span-full text-center py-16 bg-white rounded-xl border border-slate-200">
              <Globe size={64} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 mb-4">No visa programs yet. Add your first program to get started.</p>
              <button
                onClick={() => setShowForm(true)}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-all"
              >
                Add First Program
              </button>
            </div>
          ) : (
            programs.map((program) => (
              <div 
                key={program.id} 
                className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all"
                data-testid={`program-card-${program.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className="bg-slate-100 p-2 rounded-lg">
                      <Globe size={20} className="text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{program.name}</h3>
                      <p className="text-sm text-slate-500">{program.country}</p>
                    </div>
                  </div>
                </div>
                
                <span className="inline-block px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium mb-3">
                  {program.visa_type}
                </span>
                
                <p className="text-sm text-slate-600 mb-3 line-clamp-2">{program.description}</p>
                
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-1.5 text-sm text-slate-500">
                    <Calendar size={14} />
                    <span>{program.processing_time_days} days</span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEdit(program)}
                      className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
                      title="Edit program"
                      data-testid={`edit-program-${program.id}`}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(program.id)}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                      title="Delete program"
                      data-testid={`delete-program-${program.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
